import { useState, useEffect, useId, type CSSProperties, type KeyboardEvent, type ReactNode } from "react"
import { useParams, Navigate } from "react-router-dom"
import { PageSpinner, PageEmpty, RuledList, ruledRowLink } from "@/components/page"
import { NoActiveTeamEmpty } from "../components/cccd/NoActiveTeamEmpty"
import { Rocket, Egg, Cpu, Satellite, OctagonAlert, Download, ArrowRight, type LucideIcon } from "lucide-react"
import { useCccdEvents } from "../hooks/useCccdEvents"
import { useAuthStore, isStaff } from "../stores/authStore"
import { useBreadcrumbStore } from "../stores/breadcrumbStore"
import {
  getContestCategoryContent,
  CONTEST_CATEGORIES,
  type CategoryContestContent,
  type CategoryDocument,
  type EvaluationLevel,
} from "../data/cccdContestCategories"
import { cn } from "@/lib/utils"

// Cada categoría tiene su color: tiñe el encabezado, la pestaña activa, las cifras clave y los puntos
// de la misión. Son tonos claros para que el texto con ese color pase contraste sobre el azul. El
// naranja de marca queda solo para acciones; una categoría sin color propio usa el naranja.
const CATEGORY_THEME: Record<string, { color: string; icon: LucideIcon }> = {
  "categoria-1": { color: "hsl(172 62% 58%)", icon: Egg },
  "categoria-2": { color: "hsl(207 78% 68%)", icon: Cpu },
  "categoria-3": { color: "hsl(268 82% 77%)", icon: Satellite },
}
const DEFAULT_THEME = { color: "hsl(15 90% 64%)", icon: Rocket }

// Semáforo de evaluación: solo un punto junto al texto, nunca una barra de color.
const LEVEL_DOT: Record<EvaluationLevel["level"], string> = {
  verde: "bg-emerald-500",
  amarillo: "bg-amber-400",
  rojo: "bg-rose-500",
}

// ─── Building blocks ─────────────────────────────────────────────────────

// Pone en negrita solo cifras con unidad, relaciones y códigos de motor, que es
// lo que un estudiante busca al escanear el reglamento. Las palabras comunes
// (huevo, paracaídas…) no se resaltan: diluirían el énfasis.
const HIGHLIGHT_PATTERN = new RegExp(
  "(" +
    [
      String.raw`\d+(?:[.,]\d+)?\s?(?:gramos|metros|segundos|calibres|km/h|cm|mm|kg|g|m)\b`,
      String.raw`\d+:\d+`,
      String.raw`\b[A-Z]{1,2}\d{1,3}[A-Z]?-\d+[A-Z]?\b`,
    ].join("|") +
    ")",
  "gi"
)

function Highlight({ text }: { text: string }) {
  return (
    <>
      {text.split(HIGHLIGHT_PATTERN).map((part, i) =>
        i % 2 === 1 ? (
          <strong key={i} className="font-semibold text-(--cat)">
            {part}
          </strong>
        ) : (
          part
        )
      )}
    </>
  )
}

function Rows({ items, tone = "muted" }: { items: string[]; tone?: "muted" | "strong" }) {
  return (
    <ul className="grid gap-x-10 border-t border-border 2xl:grid-cols-2">
      {items.map((item, i) => (
        <li
          key={i}
          className={cn(
            "border-b border-border py-3",
            tone === "strong" ? "flex items-start gap-3 text-base text-foreground" : "text-sm text-muted-foreground"
          )}
        >
          {tone === "strong" && <span aria-hidden="true" className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-(--cat)" />}
          <span>
            <Highlight text={item} />
          </span>
        </li>
      ))}
    </ul>
  )
}

function Subhead({ children }: { children: ReactNode }) {
  return <h3 className="mb-1 text-sm font-semibold text-foreground">{children}</h3>
}

// Lista de definición con reglas finas: etiqueta a la izquierda, valor a la derecha.
function SpecList({ rows }: { rows: { label: string; value: string }[] }) {
  return (
    <dl className="divide-y divide-border border-y border-border">
      {rows.map((row) => (
        <div key={row.label} className="grid gap-1 py-3 text-sm sm:grid-cols-[12rem_1fr] sm:gap-4">
          <dt className="text-muted-foreground">{row.label}</dt>
          <dd className="font-medium text-foreground">{row.value}</dd>
        </div>
      ))}
    </dl>
  )
}

// Un nivel por línea, solo con su nombre: el detalle va aparte, a petición.
function LevelSummary({ title, levels }: { title: string; levels: EvaluationLevel[] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 py-3 text-sm">
      <span className="w-28 font-semibold text-foreground">{title}</span>
      {levels.map((l) => (
        <span key={l.level} className="inline-flex items-center gap-2 text-muted-foreground">
          <span aria-hidden className={cn("h-2 w-2 shrink-0 rounded-full", LEVEL_DOT[l.level])} />
          {l.label}
        </span>
      ))}
    </div>
  )
}

function LevelDetail({ title, levels }: { title: string; levels: EvaluationLevel[] }) {
  return (
    <div>
      <Subhead>{title}</Subhead>
      <RuledList>
        {levels.map((l) => (
          <li key={l.level} className="flex items-start gap-3 py-3 text-sm">
            <span aria-hidden className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", LEVEL_DOT[l.level])} />
            <p className="text-muted-foreground">
              <span className="font-semibold text-foreground">{l.label}.</span> {l.description}
            </p>
          </li>
        ))}
      </RuledList>
    </div>
  )
}

// Detalle a petición dentro de una pestaña. <details> nativo: teclado y lector de pantalla incluidos.
function Peek({ summary, children }: { summary: string; children: ReactNode }) {
  return (
    <details className="group">
      <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 rounded-sm text-sm font-medium text-muted-foreground outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 [&::-webkit-details-marker]:hidden">
        {summary}
        <ArrowRight aria-hidden className="h-3.5 w-3.5 transition-transform group-open:rotate-90" />
      </summary>
      <div className="mt-3 space-y-6">{children}</div>
    </details>
  )
}

// Static, hardcoded document links per category (no admin/database
// management by design) — see cccdContestCategories.ts.
function DocumentList({ documents }: { documents: CategoryDocument[] }) {
  return (
    <RuledList>
      {documents.map((doc) => (
        <li key={doc.url}>
          <a
            href={doc.url}
            target="_blank"
            rel="noreferrer"
            className={cn(ruledRowLink, "flex items-center justify-between gap-3 py-3 text-sm font-medium text-foreground")}
          >
            {doc.title}
            <Download aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
          </a>
        </li>
      ))}
    </RuledList>
  )
}

// ─── Category briefing ───────────────────────────────────────────────────

interface BriefingTab {
  id: string
  label: string
  content: ReactNode
}

function CategoryBriefing({
  scopeLabel,
  eventName,
  content,
  toolbar,
}: {
  scopeLabel: string
  eventName: string
  content: CategoryContestContent
  /** Controles del staff (selector de categoría); van dentro del encabezado. */
  toolbar?: ReactNode
}) {
  const theme = CATEGORY_THEME[content.slug] ?? DEFAULT_THEME
  const ThemeIcon = theme.icon
  const baseId = useId()
  const [active, setActive] = useState(0)
  const { rocketSpec, altimeter } = content

  const rocketRows = [
    ...(content.motorParticipations ?? []).map((p) => ({
      label: p.label,
      value: [
        `Motor ${p.motor}`,
        p.targetAltitude && `${p.targetAltitude} objetivo`,
        p.maxMass && `${p.maxMass} máx.`,
      ]
        .filter(Boolean)
        .join(" · "),
    })),
    { label: "Longitud mínima", value: rocketSpec.minLength },
    { label: "Masa máxima", value: rocketSpec.maxMass },
    ...(content.flightTimeWindow ? [{ label: "Tiempo de vuelo", value: content.flightTimeWindow }] : []),
    { label: "Recuperación", value: rocketSpec.recovery },
    { label: "Altímetro", value: `${rocketSpec.altimeter}: ${altimeter.models}` },
    { label: "Medidas del altímetro", value: `${altimeter.dimensions} · ${altimeter.weight}` },
  ]

  // Una pestaña por pregunta del estudiante, de lo urgente a lo detallado.
  const tabs: BriefingTab[] = [
    {
      id: "mision",
      label: "Misión",
      content: <Rows items={content.mission} tone="strong" />,
    },
    {
      id: "cohete",
      label: "Tu cohete",
      content: (
        <div className="space-y-6">
          <SpecList rows={rocketRows} />
          {content.providedByOrganization && (
            <div>
              <Subhead>La organización te entrega</Subhead>
              <Rows items={content.providedByOrganization} />
            </div>
          )}
        </div>
      ),
    },
    {
      id: "reglas",
      label: "Reglas",
      content: (
        <div className="space-y-6">
          {content.payloadRestrictions && (
            <div>
              <Subhead>Restricciones de la carga útil</Subhead>
              <RuledList>
                {content.payloadRestrictions.map((item, i) => {
                  // Las prohibiciones empiezan con "No …"; llevan icono y peso para que no pasen por una regla más.
                  const forbidden = item.startsWith("No ")
                  return (
                    <li key={i} className="flex items-start gap-3 py-3 text-sm text-muted-foreground">
                      {forbidden ? (
                        <>
                          <OctagonAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                          <span className="sr-only">Prohibido: </span>
                        </>
                      ) : (
                        <span aria-hidden className="w-4 shrink-0" />
                      )}
                      <span className={forbidden ? "font-semibold text-foreground" : undefined}>
                        <Highlight text={item} />
                      </span>
                    </li>
                  )
                })}
              </RuledList>
            </div>
          )}
          <div>
            <Subhead>Cómo funciona el reto</Subhead>
            <Rows items={content.challengeDetails} />
          </div>
          {content.engineeringRequirements && content.engineeringRequirements.length > 0 && (
            <div>
              <Subhead>Requisitos de ingeniería</Subhead>
              <Rows items={content.engineeringRequirements} />
            </div>
          )}
          <Peek summary="Leer la descripción completa del reto">
            <p className="text-sm text-muted-foreground">
              <Highlight text={content.intro} />
            </p>
          </Peek>
        </div>
      ),
    },
    {
      id: "evaluacion",
      label: "Evaluación",
      content: (
        <div className="space-y-6">
          <div>
            <Subhead>Qué se puntúa</Subhead>
            <Rows items={content.scoringCriteria} />
          </div>
          <div className="divide-y divide-border border-y border-border">
            <LevelSummary title="Recuperación" levels={content.recoveryEvaluation} />
            <LevelSummary title="Estabilidad" levels={content.stabilityEvaluation} />
          </div>
          <Peek summary="Qué significa cada nivel">
            <LevelDetail title="Recuperación" levels={content.recoveryEvaluation} />
            <LevelDetail title="Estabilidad" levels={content.stabilityEvaluation} />
          </Peek>
        </div>
      ),
    },
    ...(content.documents.length > 0
      ? [{ id: "documentos", label: "Documentos", content: <DocumentList documents={content.documents} /> }]
      : []),
  ]

  const tabId = (i: number) => `${baseId}-tab-${i}`
  const goTo = (i: number) => {
    setActive(i)
    document.getElementById(tabId(i))?.focus()
  }

  // Patrón ARIA de pestañas: flechas, Inicio y Fin; solo la pestaña activa entra en el orden de Tab.
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const last = tabs.length - 1
    if (e.key === "ArrowRight" || e.key === "ArrowDown") goTo(active === last ? 0 : active + 1)
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") goTo(active === 0 ? last : active - 1)
    else if (e.key === "Home") goTo(0)
    else if (e.key === "End") goTo(last)
    else return
    e.preventDefault()
  }

  const next = tabs[active + 1]

  return (
    <article className="space-y-8" style={{ "--cat": theme.color } as CSSProperties}>
      <header className="relative isolate -mx-4 -mt-4 overflow-hidden sm:-mx-6 sm:-mt-6 lg:-mx-8 lg:-mt-8">
        <div aria-hidden="true" className="absolute inset-0 -z-10 bg-linear-to-r from-(--cat)/30 via-(--cat)/10 to-transparent" />
        <ThemeIcon
          aria-hidden="true"
          strokeWidth={1}
          className="absolute -right-4 top-1/2 -z-10 h-56 w-56 -translate-y-1/2 text-(--cat)/25 sm:h-72 sm:w-72 lg:right-12"
        />
        <div className="px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
          {toolbar && <div className="mb-6">{toolbar}</div>}
          <h1 className="max-w-4xl text-balance font-display text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
            {content.challengeName}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {scopeLabel} · {eventName} · {content.title}
          </p>
        </div>
      </header>

      <div className="xl:grid xl:grid-cols-[16rem_minmax(0,1fr)] xl:items-start xl:gap-12">
        <div
          role="tablist"
          aria-label="Información de tu categoría"
          onKeyDown={onKeyDown}
          className="glass-chip inline-flex max-w-full gap-1 overflow-x-auto rounded-full p-1 xl:sticky xl:top-20 xl:flex xl:flex-col xl:overflow-visible xl:rounded-xl"
        >
          {tabs.map((tab, i) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={tabId(i)}
              aria-selected={active === i}
              aria-controls={`${baseId}-panel-${i}`}
              tabIndex={active === i ? 0 : -1}
              onClick={() => setActive(i)}
              className={cn(
                "min-h-11 shrink-0 rounded-full px-4 text-sm font-semibold outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/50 sm:min-h-9 xl:w-full xl:rounded-lg xl:text-left",
                active === i
                  ? "bg-(--cat)/20 text-foreground ring-1 ring-(--cat)/40"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div>
        {tabs.map((tab, i) => (
          <div
            key={tab.id}
            role="tabpanel"
            id={`${baseId}-panel-${i}`}
            aria-labelledby={tabId(i)}
            hidden={active !== i}
            className="pt-5 xl:pt-0"
          >
            {active === i && <div className="motion-fade">{tab.content}</div>}
          </div>
        ))}

        {next && (
          <button
            type="button"
            onClick={() => goTo(active + 1)}
            className="mt-6 inline-flex items-center gap-2 rounded-md text-sm font-semibold text-foreground outline-none hover:text-(--cat) focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            Siguiente: {next.label}
            <ArrowRight aria-hidden className="h-4 w-4" />
          </button>
        )}
        </div>
      </div>
    </article>
  )
}

function EmptyState() {
  return (
    <NoActiveTeamEmpty
      heading="CCCD"
      icon={<Rocket className="h-8 w-8 text-primary" />}
      description="Cuando tu equipo esté activo en un evento nacional o regional del Concurso Colombiano de Cohetería y Diseño, aquí verás el reto y los requisitos de tu categoría."
      withActions
    />
  )
}

// Staff isn't tied to one team's category (/cccd/me/events returns
// categorySlug: null for staff), so the page offers a lightweight selector
// over the 3 static categories instead — see
// openspec/changes/academy-staff-unrestricted-access design.md Decision 4.
// Es un filtro, no pestañas: no controla un panel propio, así que usa aria-pressed.
function StaffCategorySelector({
  selected,
  onSelect,
}: {
  selected: string
  onSelect: (slug: string) => void
}) {
  return (
    <div role="group" aria-label="Categoría que quieres ver (solo staff)" className="flex flex-wrap gap-1">
      {Object.values(CONTEST_CATEGORIES).map((category) => (
        <button
          key={category.slug}
          type="button"
          aria-pressed={selected === category.slug}
          onClick={() => onSelect(category.slug)}
          className={cn(
            "rounded-md px-3 py-1.5 text-sm font-medium outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/50",
            selected === category.slug ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          {category.title}
        </button>
      ))}
    </div>
  )
}

export default function CccdPage() {
  const { eventId } = useParams<{ eventId?: string }>()
  const user = useAuthStore((s) => s.user)
  const isStaffUser = isStaff(user)
  const { nationalEvents, regionalEvents, isLoading } = useCccdEvents()
  const [staffCategorySlug, setStaffCategorySlug] = useState<string>("categoria-1")

  const allEvents = [
    ...nationalEvents.map((e) => ({ ...e, scopeLabel: "Evento nacional" as const })),
    ...regionalEvents.map((e) => ({ ...e, scopeLabel: "Evento regional" as const })),
  ]
  const currentEvent = allEvents.find((e) => e.id === eventId) ?? allEvents[0] ?? null
  const setBreadcrumbLabel = useBreadcrumbStore((s) => s.setLabel)

  useEffect(() => {
    setStaffCategorySlug(currentEvent?.categorySlug ?? "categoria-1")
  }, [currentEvent?.id, currentEvent?.categorySlug])

  useEffect(() => {
    if (currentEvent) setBreadcrumbLabel(`/cccd/${currentEvent.id}`, currentEvent.name)
  }, [currentEvent, setBreadcrumbLabel])

  if (isLoading) {
    return (
      <PageSpinner />
    )
  }

  if (!currentEvent) {
    return <EmptyState />
  }

  if (eventId !== currentEvent.id) {
    return <Navigate to={`/cccd/${currentEvent.id}`} replace />
  }

  const categorySlug = isStaffUser ? staffCategorySlug : currentEvent.categorySlug
  const content = getContestCategoryContent(categorySlug)

  return (
    <div className="space-y-8">
      {content ? (
        <CategoryBriefing
          // Al cambiar de evento o de categoría el briefing vuelve a su primera pestaña.
          key={`${currentEvent.id}:${content.slug}`}
          scopeLabel={currentEvent.scopeLabel}
          eventName={currentEvent.name}
          content={content}
          toolbar={isStaffUser ? <StaffCategorySelector selected={staffCategorySlug} onSelect={setStaffCategorySlug} /> : undefined}
        />
      ) : (
        // La categoría de este evento no tiene reglas cargadas en la Academia: mejor decirlo que dejar la página en blanco.
        <PageEmpty
          title="Aún no hay información para tu categoría"
          description="Cuando se publique el reglamento de tu categoría lo verás aquí. Si crees que ya debería estar, escríbenos a info@satelab.org."
        />
      )}
    </div>
  )
}
